<!--
@component
Squads

See markup below.
-->

<script lang="ts">
  type SquadMember = {
    firstName?: string;
    lastName?: string;
    matchName?: string;
    role?: string;
    position?: string;
    shirtNumber?: string | number;
    [key: string]: unknown;
  };

  type Position = 'Goalkeeper' | 'Defender' | 'Midfielder' | 'Attacker';

  let { squad }: { squad: SquadMember[] } = $props();

  /** Safe accessor — components rendered before the loader resolves can pass `undefined`. */
  let members: SquadMember[] = $derived(Array.isArray(squad) ? squad : []);

  /** Full name from first + last; used for coaches and assistants. */
  const fullName = (m: SquadMember) =>
    `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();

  /** Players for a given position who have been issued a shirt number. */
  const playersFor = (position: Position) =>
    members.filter((m) => m.position === position && m.shirtNumber);

  let coaches: string[] = $derived(
    members.filter((m) => m.role === 'coach').map(fullName)
  );

  let assistantCoaches: string[] = $derived(
    members
      .filter((m) => m.role === 'assistant coach')
      .map((m) => m.matchName ?? fullName(m))
  );

  let goalkeepers = $derived(playersFor('Goalkeeper'));
  let defenders = $derived(playersFor('Defender'));
  let midfielders = $derived(playersFor('Midfielder'));
  let attackers = $derived(playersFor('Attacker'));

  let hasPlayers = $derived(
    goalkeepers.length +
      defenders.length +
      midfielders.length +
      attackers.length >
      0
  );

  /**
   * Ordered position groupings rendered below. Splitting goalkeepers/defenders
   * into one row and midfielders/attackers into the next mirrors the rough
   * pitch layout the design calls for.
   */
  let positionRows = $derived([
    [
      { id: 'goalkeepers', label: 'Goalkeeper', players: goalkeepers },
      { id: 'defenders', label: 'Defender', players: defenders },
    ],
    [
      { id: 'midfielders', label: 'Midfielder', players: midfielders },
      { id: 'attackers', label: 'Attacker', players: attackers },
    ],
  ]);
</script>

{#snippet coachRow(label: string, value: string)}
  <div class="grid-row">
    <div class="grid-item">
      <span class="font-medium text-secondary">{label}:</span>
    </div>
    <div class="grid-item">{value}</div>
  </div>
{/snippet}

{#snippet positionColumn(id: string, label: string, players: SquadMember[])}
  <div class="col-auto" {id}>
    <div class="text-sm font-medium mb-2 text-secondary">{label}</div>
    <ul class="list-group">
      {#each players as player (player.matchName ?? fullName(player))}
        <li class="list-group-item">{player.matchName ?? fullName(player)}</li>
      {/each}
    </ul>
  </div>
{/snippet}

{#if coaches.length > 0 || assistantCoaches.length > 0}
  <div class="coaches w-full fmx-auto text-sm fmb-2">
    <div class="grid-container">
      {#if coaches.length > 0}
        {@render coachRow('Manager', coaches.join(', '))}
      {/if}
      {#if assistantCoaches.length > 0}
        {@render coachRow(
          assistantCoaches.length > 1 ? 'Assistants' : 'Assistant',
          assistantCoaches.join(', ')
        )}
      {/if}
    </div>
  </div>
{/if}

{#if hasPlayers}
  <div class="container w-full fmx-auto squad">
    {#each positionRows as row, rowIndex (rowIndex)}
      <div class="row">
        {#each row as col (col.id)}
          {@render positionColumn(col.id, col.label, col.players)}
        {/each}
      </div>
    {/each}
  </div>
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;
  .coaches {
    display: flex;
    justify-content: center;
    .grid-container {
      display: inline-grid;
      grid-template-columns: auto auto;
      grid-gap: 2px 8px;
      .grid-row {
        display: contents;
      }
      @media (max-width: 600px) {
        display: flex;
        flex-direction: column;
        align-items: center;
        grid-gap: 0;
        .grid-row {
          display: flex;
          justify-content: center;
          width: 100%;
          gap: 4px;
        }
        .grid-item {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          @media (max-width: 410px) {
            max-width: 225px;
          }
          @media (max-width: 375px) {
            max-width: 200px;
          }
        }
      }
    }
  }
  .squad {
    display: flex;
    justify-content: center;

    @media (max-width: 660px) {
      flex-direction: column;
      align-items: center;
    }

    .row {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      max-width: max-content;
      justify-content: space-around;
      background-color: var(--theme-colour-muted-grey);

      &:first-child {
        border-top-left-radius: var(--theme-style-border-radius);
        border-bottom-left-radius: var(--theme-style-border-radius);
      }
      &:last-child {
        border-top-right-radius: var(--theme-style-border-radius);
        border-bottom-right-radius: var(--theme-style-border-radius);
      }

      @media (min-width: 999px) {
        min-width: 25rem;
      }

      @media (max-width: 660px) {
        min-width: 100%;
        background-color: var(--theme-colour-background);
        &:first-child {
          border-radius: 0;
          border-top-left-radius: var(--theme-style-border-radius);
          border-top-right-radius: var(--theme-style-border-radius);
        }
        &:last-child {
          border-radius: 0;
          border-bottom-left-radius: var(--theme-style-border-radius);
          border-bottom-right-radius: var(--theme-style-border-radius);
        }
      }
    }

    .col-auto {
      width: auto;
      max-width: 100%;

      @include mixins.fmx-3;
      @include mixins.fmy-5;

      @media (max-width: 999px) {
        min-width: 50%;
      }

      @media (max-width: 660px) {
        text-align: center;
        @include mixins.fpy-5;
        @include mixins.fpx-3;
        margin: 0.75rem;
        width: 100%;
      }

      border-radius: var(--theme-style-border-radius);

      background-color: var(--theme-colour-muted-grey);
    }

    ul {
      list-style-type: none;
      padding-left: 0;
      margin-bottom: 0;
      @include mixins.font-sans;
      @include mixins.text-sm;
      li {
        @include mixins.leading-tighter;

        @media (min-width: 360px) and (max-width: 660px) {
          display: inline-flex;

          &:not(:last-child):after {
            content: '•';
            color: var(--theme-colour-tan-grey);
            @include mixins.fmx-1;
          }
        }
      }
    }
  }
</style>
